import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Popover, message } from 'antd';
import { ChevronLeft, Check, ScanFace, ArrowRight, Info, X } from 'lucide-react';
import { screen1Icons } from './svAssets';
import VideoCaptureComponent from './VideoCapture';
import QuizComponent from './QuizComponent';
import PatientDetailsCard, { validateDetails } from './PatientDetailsCard';
import SmartVitalsReport from './SmartVitalsReport';
import { Dialog } from 'primereact/dialog';
import Requirementimg from '../../Assets/Images/AiReport/requirements.png';
import http from '../../api/Services/HttpCommon';
import './smartVitalsFlow.css';

dayjs.extend(customParseFormat);

const QUANTUM_URL = process.env.REACT_APP_QUANTUMURL;

const STEPS = ['Edit details &\nChoose flow', 'Wellness Check\n(Can skip)', 'Face Scan', 'Get Reports'];
const STEP_INDEX = { details: 0, wellness: 1, scan: 2 };

const HEADINGS = {
  details: {
    title: <>Start your <span>SmartVitals check</span></>,
    subtitle: 'Confirm your details, then choose what to check.',
  },
  wellness: {
    title: <>Mental Wellness <span>Check</span></>,
    subtitle: 'Tap one answer per card. There are no right or wrong answers. Choose what feels closest to the last two weeks.',
  },
  scan: {
    title: <>Get your <span>Face Scan</span></>,
    subtitle: 'Keep your face centered in the circle for accurate detection while we record for 30 seconds.',
  },
};

const VITALS = ['Heart rate', 'Blood pressure', 'Oxygen (SpO2)', 'HRV', 'Breathing rate', 'Haemoglobin', 'Stress index'];

const parseDob = (raw) => {
  if (!raw) return null;
  const d = dayjs(raw, ['YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY'], true);
  if (d.isValid()) return d;
  const loose = dayjs(raw);
  return loose.isValid() ? loose : null;
};

// "151.0" -> "151"; the form only takes whole numbers.
const toWhole = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? String(Math.round(n)) : '';
};

// The patient-details API sends gender as a word ("Female"/"Male"/"Others") while the login
// profile and the details card use codes ("F"/"M"/"O"). Normalise to the code.
const toGenderCode = (g) => {
  const c = String(g || '').trim().charAt(0).toUpperCase();
  return c === 'F' || c === 'M' || c === 'O' ? c : '';
};

const toBool = (v) => (v === true || v === 'true' ? true : v === false || v === 'false' ? false : null);

// Used until (or if) the patient-details API has nothing for this patient.
const detailsFromLogin = (user) => ({
  firstName: user?.firstName || '',
  lastName: user?.lastName || '',
  patientId: '',
  dob: parseDob(user?.dob),
  gender: toGenderCode(user?.gender),
  height: '',
  weight: '',
  diabetes: null,
  smoke: null,
});

const Stepper = ({ current }) => (
  <div className="sv-stepper" aria-label="Progress">
    {STEPS.map((label, i) => (
      <div key={label} style={{ display: 'contents' }}>
        {i > 0 && <div className={`sv-step-line${i < current ? ' is-done' : i === current ? ' is-active' : ''}`} />}
        <div className={`sv-step${i < current ? ' is-done' : i === current ? ' is-active' : ''}`}>
          <div className="sv-step-dot">{i === current ? <Check size={12} strokeWidth={3} /> : i + 1}</div>
          <div className="sv-step-label" style={{ whiteSpace: 'pre-line' }}>{label}</div>
        </div>
      </div>
    ))}
  </div>
);

const SmartVitalsChecker = () => {
  const navigate = useNavigate();
  const [userData] = useState(() => JSON.parse(localStorage.getItem('loginObj') || '{}'));

  const [step, setStep] = useState('details');
  const [withWellness, setWithWellness] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);
  const [onUploadPage, setOnUploadPage] = useState(false);

  const [details, setDetails] = useState(() => detailsFromLogin(userData));
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [prefilled, setPrefilled] = useState(false);
  const [edited, setEdited] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [lastReport, setLastReport] = useState(null);

  // Latest saved details for this patient. Edits aren't saved separately —
  // they go out with the /smartvital submit, and the next fetch returns them.
  useEffect(() => {
    if (!userData?.code) {
      setLoadingDetails(false);
      return;
    }
    let active = true;
    axios
      .get(`${QUANTUM_URL}smartvital/patient-details/${userData.code}`, { headers: { accept: '*/*' } })
      .then((res) => {
        const d = res?.data?.status === 'success' ? res.data.data : null;
        if (!d) return null;
        const login = detailsFromLogin(userData);
        return {
          firstName: d.first_name || login.firstName,
          lastName: d.last_name || login.lastName,
          patientId: d.patient_id || '',
          dob: parseDob(d.dob) || login.dob,
          gender: toGenderCode(d.gender) || login.gender,
          height: toWhole(d.height),
          weight: toWhole(d.weight),
          diabetes: toBool(d.diabetes),
          smoke: toBool(d.smoke),
        };
      })
      .catch((err) => {
        console.error('Failed to fetch smartvital patient details:', err);
        return null;
      })
      .then((fetched) => {
        if (!active) return;
        const next = fetched || detailsFromLogin(userData);
        setDetails(next);
        setPrefilled(Boolean(fetched));
        setLoadingDetails(false);
        // Always land on the summary card; the form opens only when the user taps Edit.
        // Missing/invalid details are still caught by ensureReady() when a scan is started.
      });
    return () => {
      active = false;
    };
  }, [userData]);

  useEffect(() => {
    if (!userData?.code) return;
    let active = true;
    http
      .get(`${QUANTUM_URL}get_report_list`, {
        params: { patientid: userData.code, service: 'Smart Vitals', page_number: 1, page_size: 10 },
      })
      .then((res) => {
        const rows = res?.data?.data || [];
        const latest = [...rows].sort(
          (a, b) => new Date(b.CREATEDDATE || b.created_date || 0) - new Date(a.CREATEDDATE || a.created_date || 0)
        )[0];
        if (active && latest) setLastReport(latest);
      })
      .catch((err) => console.error('Failed to fetch last smart vitals report:', err));
    return () => {
      active = false;
    };
  }, [userData?.code]);

  const goToReports = () => navigate('/aireports', { state: { tab: 'smart_vitals' } });

  // Same rule as the AI Reports page: a Smart Vitals report is viewable once STATUS is 1.
  const lastReportStatus = Number(lastReport?.STATUS ?? lastReport?.status);
  const lastReportState = !lastReport
    ? null
    : lastReportStatus === 1 ? 'ready' : lastReportStatus === 2 ? 'failed' : 'pending';
  const [showLastReport, setShowLastReport] = useState(false);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const ensureReady = () => {
    if (loadingDetails) return false;
    if (isEditing) {
      message.warning('Save your details first.');
      return false;
    }
    // Never switch the card into the form automatically — the user opens it via Edit.
    if (Object.keys(validateDetails(details)).length > 0) {
      message.warning('Please complete your details first — tap Edit to update them.');
      return false;
    }
    return true;
  };

  const startFaceScan = () => {
    if (!ensureReady()) return;
    // No questionnaire this run — drop answers left over from an earlier one.
    sessionStorage.removeItem('calculatedAnswers');
    setWithWellness(false);
    goToScan();
  };

  const startWellness = () => {
    if (!ensureReady()) return;
    setWithWellness(true);
    setStep('wellness');
    scrollTop();
  };

  const skipWellness = () => {
    sessionStorage.removeItem('calculatedAnswers');
    goToScan();
  };

  // Requirements popover opens on arrival; the (i) button brings it back.
  const goToScan = () => {
    setStep('scan');
    setShowRequirements(true);
    scrollTop();
  };

  const handleBack = () => {
    if (step === 'details') navigate('/smartvital');
    else if (step === 'wellness') setStep('details');
    else setStep(withWellness ? 'wellness' : 'details');
    scrollTop();
  };

  const handleSave = (draft) => {
    setDetails(draft);
    setEdited(true);
    setIsEditing(false);
  };

  // Shape VideoCapture already expects for the /smartvital payload.
  const formData = {
    dob: details.dob ? details.dob.format('MM/DD/YYYY') : undefined,
    gender: details.gender,
    heightCm: details.height ? parseFloat(details.height).toFixed(2) : undefined,
    heightUnit: 'cm',
    weight: details.weight,
    weightUnit: 'kg',
    diabetes: details.diabetes,
    smoke: details.smoke,
  };

  const heading = HEADINGS[step];

  return (
    <div className="sv-page">
      <div className="sv-header" style={step === 'scan' && onUploadPage ? { display: 'none' } : undefined}>
        <div>
          <div className="sv-title-row">
            <button type="button" className="sv-back" onClick={handleBack} aria-label="Back">
              <ChevronLeft size={26} />
            </button>
            <h1 className="sv-title">{heading.title}</h1>
          </div>
          <p className="sv-subtitle">{heading.subtitle}</p>
        </div>
        <div className="sv-header-right">
          <Stepper current={STEP_INDEX[step]} />
          {step === 'scan' && (
            <Popover
              open={showRequirements}
              onOpenChange={setShowRequirements}
              trigger="click"
              placement="bottomRight"
              rootClassName="sv-reqpop-overlay"
              content={
                <div className="sv-reqpop">
                  <button type="button" className="sv-reqpop-close" onClick={() => setShowRequirements(false)} aria-label="Close">
                    <X size={12} />
                  </button>
                  <div className="sv-reqpop-head"><ScanFace size={16} /> Requirements for Face Video Recording</div>
                  <img src={Requirementimg} alt="" />
                  <h4>Face Recording Requirements</h4>
                  <ul>
                    <li>You will need to record your face for 30 seconds.</li>
                    <li>Keep your face well-lit, centered, and still.</li>
                    <li>Maintain a neutral expression.</li>
                    <li>Avoid any movement during the recording.</li>
                  </ul>
                  <h4>Internet Requirements</h4>
                  <ul>
                    <li>Ensure you have a stable internet connection.</li>
                    <li>Use a device with a working camera and microphone.</li>
                  </ul>
                </div>
              }
            >
              <button type="button" className="sv-info-btn" aria-label="Recording requirements">
                <Info size={16} />
              </button>
            </Popover>
          )}
        </div>
      </div>

      {step === 'details' && (
        <div className="sv-layout">
          <PatientDetailsCard
            details={details}
            loading={loadingDetails}
            isEditing={isEditing}
            prefilled={prefilled}
            edited={edited}
            lastReport={lastReport}
            onEdit={() => setIsEditing(true)}
            onCancel={() => setIsEditing(false)}
            onSave={handleSave}
            lastReportState={lastReportState}
            onViewReport={() => setShowLastReport(true)}
          />

          <div>
            <div className="sv-choose-head">
              <h2 className="sv-choose-title">What would you like to check today?</h2>
              <button type="button" className="sv-link" onClick={goToReports}>
                My AI Reports
              </button>
            </div>

            <div className="sv-options">
              <div className="sv-card sv-option is-primary">
                <div className="sv-option-top">
                  <img className="sv-option-icon" src={screen1Icons.faceScan} alt="" />
                  <span className="sv-badge">Recommended</span>
                </div>
                <h3>Vitals Face Scan</h3>
                <p className="sv-option-desc">
                  Record a 30-second video of your face. Our AI reads blood flow under the skin to estimate your vitals.
                </p>
                <div className="sv-chips">
                  {VITALS.map((v) => <span key={v} className="sv-chip">{v}</span>)}
                </div>
                <div className="sv-option-foot">
                  <button type="button" className="sv-btn sv-btn-primary" onClick={startFaceScan}>
                    Start face scan <ArrowRight size={16} />
                  </button>
                  <span className="sv-time"><img src={screen1Icons.time} alt="" /> 30 sec</span>
                </div>
              </div>

              {/* Full check = the wellness questions, then the same face scan. */}
              <div className="sv-card sv-option">
                <div className="sv-option-top">
                  <img className="sv-option-icon" src={screen1Icons.mentalWellness} alt="" />
                  <span className="sv-badge is-green">Most complete</span>
                </div>
                <h3 className="is-green">Full Health Check</h3>
                <p className="sv-option-desc">
                  Face scan plus 8 quick wellness questions. Get your vitals and stress, sleep and fatigue insights in one report.
                </p>
                <ol className="sv-steps">
                  <li>
                    <span className="sv-steps-num">1</span>
                    <span><b>Wellness questions</b> 8 taps, about 2 min</span>
                  </li>
                  <li>
                    <span className="sv-steps-num">2</span>
                    <span><b>Vitals face scan</b> opens right after, 30 sec</span>
                  </li>
                </ol>
                <div className="sv-option-foot">
                  <button type="button" className="sv-btn sv-btn-ghost" onClick={startWellness}>
                    Start full check <ArrowRight size={16} />
                  </button>
                  <span className="sv-time"><img src={screen1Icons.time} alt="" /> 3 min</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Same viewer the AI Reports page opens for a Smart Vitals report. */}
      <Dialog
        header="Smart Vitals Report"
        visible={showLastReport}
        style={{ width: '90vw', height: '90vh', maxWidth: '1400px' }}
        breakpoints={{ '768px': '95vw' }}
        onHide={() => setShowLastReport(false)}
      >
        {showLastReport && lastReport && <SmartVitalsReport vitalsData={lastReport} isModal={true} />}
      </Dialog>

      {step === 'wellness' && <QuizComponent onQuizSubmit={goToScan} onSkip={skipWellness} />}

      {step === 'scan' && (
        <VideoCaptureComponent formData={formData} onCancel={handleBack} onUploadPageChange={setOnUploadPage} />
      )}

    </div>
  );
};

export default SmartVitalsChecker;
